import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Crosshair, Loader2, Repeat, X } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { TRIAL_KEY, getTrial } from "@/lib/trial";

const EXAMPLE = "Got caught in triangles twice, knee cut worked a few times, couldn't keep mount.";

const ROWS = [
  { key: "worked", label: "Worked", color: "#10B981", icon: Check, empty: "Nothing clearly went well in this note." },
  { key: "problem", label: "Problem", color: "#EF4444", icon: X, empty: "No clear problem mentioned." },
  { key: "focus", label: "Focus next", color: "#F59E0B", icon: Crosshair, empty: "Add what went wrong and why for a sharper focus." },
  { key: "drill", label: "Drill next", color: "#3B82F6", icon: Repeat, empty: "No drill suggested yet." },
];

function Result({ r }) {
  return (
    <div className="space-y-2.5" data-testid="try-result">
      <div className="font-display text-xl uppercase tracking-tight">{r.title}</div>
      {ROWS.map((row) => (
        <div key={row.key} className="px-4 py-3 rounded-r-xl border-l-2" style={{ borderColor: row.color, background: `${row.color}12` }} data-testid={`try-result-${row.key}`}>
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: row.color }}><row.icon size={11} strokeWidth={3} /> {row.label}</div>
          {(r[row.key] || []).length ? r[row.key].map((t) => <div key={t} className="text-[14px] text-[#E4E4E7] mt-1 leading-snug">{t}</div>)
            : <div className="text-[13px] text-[#71717A] mt-1">{row.empty}</div>}
        </div>
      ))}
    </div>
  );
}

export function TryIt() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(getTrial);

  const run = async () => {
    if (text.trim().length < 15 || busy) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/try/analyse", { text: text.trim() });
      localStorage.setItem(TRIAL_KEY, JSON.stringify(data));
      setResult(data);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="try" className="border-t border-[#1F1F23] bg-[#0D0D0E]" data-testid="landing-try">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-xl mx-auto px-5 md:px-8 py-20">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#E50914] font-semibold mb-3">Try it now · no account</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold uppercase tracking-tight leading-none">How did your last<br />session go?</h2>
        {!result ? (
          <div className="mt-7">
            <textarea data-testid="try-textarea" value={text} onChange={(e) => setText(e.target.value)} maxLength={3000} placeholder={EXAMPLE}
              className="w-full min-h-[140px] bg-[#111113] border border-[#27272A] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914] rounded-2xl p-4 text-[16px] leading-relaxed text-white placeholder:text-[#52525B] outline-none resize-none transition-colors" />
            {error && <p className="text-sm text-[#FF6B6B] mt-2" data-testid="try-error">{error}</p>}
            <button data-testid="try-submit" onClick={run} disabled={text.trim().length < 15 || busy} className="btn-primary mt-4">
              {busy ? <><Loader2 size={18} className="animate-spin" /> Analysing…</> : "Analyse my session"}
            </button>
            <p className="text-xs text-[#71717A] mt-3">One free analysis. Messy notes are fine.</p>
          </div>
        ) : (
          <div className="mt-7 space-y-6">
            <Result r={result} />
            <div className="card p-5" data-testid="try-save-cta">
              <div className="font-semibold">Save your session and start tracking your progress</div>
              <p className="text-sm text-[#A1A1AA] mt-1">Create your free account. This analysis goes straight into your journal.</p>
              <Link to="/register" data-testid="try-create-account" className="btn-primary mt-4">Create free account <ArrowRight size={18} /></Link>
            </div>
          </div>
        )}
      </motion.div>
    </section>
  );
}
